import { useEffect, useMemo, useRef } from 'react';
import { partesDelDia, fechaLarga } from '../lib/formato.js';
import { DiasCargando } from '../componentes/Cargando.jsx';
import { Estado } from '../componentes/Estado.jsx';

export function PasoDia({
  dias, cargando, error, elegido, onElegir, onPreseleccionar, onReintentar, timezone
}) {
  const contenedor = useRef(null);
  const yaSalto = useRef(false);

  // El mes se muestra sólo cuando cambia respecto del día anterior. Repetir
  // "AGO" treinta veces es ruido; lo que importa es el corte de mes.
  const tarjetas = useMemo(() => {
    let mesPrevio = null;

    return dias.map((dia) => {
      const partes = partesDelDia(dia.fecha, timezone);
      const mostrarMes = partes.mes !== mesPrevio;
      mesPrevio = partes.mes;
      return { ...dia, ...partes, mostrarMes };
    });
  }, [dias, timezone]);

  // Apenas se sabe cuál es el primer día con turnos, queda marcado. Buscar a
  // mano el primer día libre entre treinta es justo lo que hay que evitar.
  // Se marca, no se avanza: la persona sigue viendo el calendario.
  useEffect(() => {
    if (yaSalto.current || elegido) return;

    const primero = dias.find((dia) => dia.libre === true);
    if (!primero) return;

    yaSalto.current = true;
    onPreseleccionar(primero.fecha);
  }, [dias, elegido, onPreseleccionar]);

  useEffect(() => {
    if (!elegido || !contenedor.current) return;
    contenedor.current
      .querySelector(`[data-fecha="${elegido}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [elegido]);

  if (error) {
    return (
      <Estado icono="sinSenal" titulo="No pudimos ver la agenda" accion={onReintentar} etiquetaAccion="Probar de nuevo">
        <p>{error}</p>
      </Estado>
    );
  }

  if (dias.length === 0) return <DiasCargando />;

  const hayAlguno = dias.some((dia) => dia.libre === true);

  return (
    <>
      <div className="dias" ref={contenedor} role="group" aria-label="Elegí el día">
        {tarjetas.map((dia) => {
          const sinTurnos = dia.libre === false;
          const sinSaber = dia.libre === null;

          return (
            <button
              key={dia.fecha}
              type="button"
              data-fecha={dia.fecha}
              className="dia"
              disabled={sinTurnos || sinSaber}
              aria-pressed={elegido === dia.fecha}
              // La fecha completa siempre, aunque en pantalla el mes esté
              // implícito: quien usa lector de pantalla no ve la columna de al lado.
              aria-label={`${fechaLarga(dia.fecha, timezone)}${sinTurnos ? ', sin turnos disponibles' : ''}`}
              onClick={() => onElegir(dia.fecha)}
            >
              <span className="dia-semana">{dia.diaSemana}</span>
              <span className="dia-numero">{dia.numero}</span>
              <span className="dia-mes">{dia.mostrarMes ? dia.mes : ''}</span>
            </button>
          );
        })}
      </div>

      {cargando && <p className="ayuda">Buscando días con turnos libres…</p>}

      {!cargando && !hayAlguno && (
        <Estado icono="calendario" titulo="No hay turnos en los próximos 30 días">
          <p>Escribile al negocio para ver si te puede hacer un lugar.</p>
        </Estado>
      )}

      {!cargando && hayAlguno && elegido && (
        <p className="ayuda" style={{ marginTop: 14 }}>
          Tocá el día para ver los horarios.
        </p>
      )}
    </>
  );
}
